import React, { useState, useMemo, useCallback } from "react";
import {
  Col,
  Flex,
  Input,
  Modal,
  notification,
  Row,
  Spin,
  Tag,
  Typography,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import {
  getProductOrderDetails,
  makeRefund,
  sendWaitingTimeNotify,
  updateProductOrders,
} from "../../../service";
import { formatedOrderDetails, formatAddressWithApartment } from "../../organisms/orders-list/OrdersList.utils";
import dayjs from "dayjs";
import { useResponsive } from "../../../hooks/useResponsive";
import { useGroupedOrderItems } from "../../../hooks/useGroupedOrderItems";
import {
  StyledButton,
  OrderSection,
  OrderItem,
  SectionTitle,
} from "../order-modals/OrderModal.styles";
import { colors } from "../../../styles/theme";
import { useBranchContext } from "../../../contexts/branch-context";
import { currencySymbol } from "../../../utils/currency";

/**
 * Optymalizacja: Stałe opcje czasowe poza komponentem
 * Wpływ na wydajność: Zapobiega tworzeniu nowej tablicy przy każdym renderze
 */
const TIME_OPTIONS = [
  { label: "10 min", value: 10 },
  { label: "20 min", value: 20 },
  { label: "30 min", value: 30 },
  { label: "45 min", value: 45 },
  { label: "60 min", value: 60 },
  { label: "75 min", value: 75 },
  { label: "90 min", value: 90 },
] as const;

interface NewOrderModalProps {
  orderId: string | null;
  open: boolean;
  onAccept: (timeValue: any) => void;
  onReject: () => void;
  onKeepInQueue: () => void;
  onComplete: () => void;
}

export const NewOrderModal: React.FC<NewOrderModalProps> = ({
  orderId,
  open,
  onAccept,
  onReject,
  onKeepInQueue,
  onComplete,
}) => {
  const { t } = useTranslation();
  const { currentBranch } = useBranchContext();
  const curSymbol = currencySymbol(currentBranch?.currency);
  const [details, setDetails] = useState<any>();
  const { isMobile } = useResponsive();
  const query = useQueryClient();

  /**
   * Optymalizacja: Wykorzystanie wspólnego hooka do grupowania pozycji
   */
  const groupedItems = useGroupedOrderItems(details?.cart || []);

  /**
   * Calculate total discount amount on frontend
   */
  const calculatedDiscount = useMemo(() => {
    const appliedCoupon = details?.header?.appliedCoupon;
    if (!appliedCoupon) return 0;

    const isSpecificProducts = appliedCoupon.scope === 'SPECIFIC_PRODUCTS';
    const affectedProductIds = appliedCoupon.affectedProductIds || [];

    // Calculate total price for affected items only
    let affectedItemsTotal = 0;
    groupedItems.forEach(group => {
      const productId = group[0]?.productId;
      const itemPrice = group[0]?.price * group[0]?.quantity;

      if (!isSpecificProducts || (productId && affectedProductIds.includes(productId))) {
        affectedItemsTotal += itemPrice;
      }
    });

    // Calculate discount based on coupon type
    if (affectedItemsTotal > 0) {
      if (appliedCoupon.type === 'PERCENTAGE') {
        return Math.round((affectedItemsTotal * appliedCoupon.value) / 100);
      } else {
        // FIXED_AMOUNT
        return Math.min(appliedCoupon.value, affectedItemsTotal);
      }
    }

    return 0;
  }, [details?.header?.appliedCoupon, groupedItems]);

  const { isLoading: isLoadingDetails } = useQuery<any, Error>(
    [`/product-order-details`, orderId],
    () => getProductOrderDetails({ id: orderId }),
    {
      onSuccess(data) {
        setDetails(formatedOrderDetails(data));
      },
      enabled: !!orderId && open,
    }
  );

  const {
    mutateAsync: refundMutateAsync,
    isLoading: refundIsLoading,
  } = useMutation(makeRefund, {
    onSuccess: () => {
      query.invalidateQueries([`/product-order-details`]);
      query.invalidateQueries([`/product-orders`]);
      notification.success({
        message: t("newOrderModal.refundSuccess"),
        style: {
          background: colors.successBackground,
        },
        placement: "bottomRight",
      });
      onReject();
    },
    onError: () => {
      notification.error({
        message: t("newOrderModal.refundError"),
        style: {
          background: colors.errorBackground,
        },
        placement: "bottomRight",
      });
    },
  });

  const {
    mutateAsync: updateOrderMutateAsync,
    isLoading: updateOrderIsLoading,
  } = useMutation(updateProductOrders, {
    onSuccess: () => {
      query.invalidateQueries([`/product-orders`]);
    },
  });

  const {
    mutateAsync: sentNotifyMutateAsync,
    isLoading: sentNotifyIsLoading,
  } = useMutation(sendWaitingTimeNotify, {
    onSuccess: () => {
      notification.success({
        message: t("newOrderModal.notifySent"),
        style: {
          background: colors.successBackground,
        },
        placement: "bottomRight",
      });
    },
  });

  /**
   * Optymalizacja: Handlery z useCallback
   * Wpływ na wydajność: Stabilne referencje funkcji zapobiegają rerenderom
   */
  const handleAcceptWithTime = useCallback(async (timeValue: any) => {
    if (!orderId) return;

    await updateOrderMutateAsync({
      id: orderId,
      body: {
        status: "IN_PROGRESS",
      },
    });

    await sentNotifyMutateAsync({
      productOrder: orderId,
      time: timeValue,
    });

    onAccept(timeValue);
  }, [orderId, updateOrderMutateAsync, sentNotifyMutateAsync, onAccept]);

  const handleConfirmOnly = useCallback(async () => {
    if (!orderId) return;

    await updateOrderMutateAsync({
      id: orderId,
      body: {
        status: "IN_PROGRESS",
      },
    });

    await sentNotifyMutateAsync({
      productOrder: orderId,
      time: "CONFIRM",
    });

    onAccept("CONFIRM");
  }, [orderId, updateOrderMutateAsync, sentNotifyMutateAsync, onAccept]);

  const handleReject = useCallback(() => {
    if (!orderId || !details) return;

    if (details.header.paymentsType === "OFFLINE") {
      updateOrderMutateAsync({
        id: orderId,
        body: {
          status: "DONE",
        },
      });
      sentNotifyMutateAsync({
        productOrder: orderId,
        time: "REJECT",
      });
      onReject();
    } else if (details.header.paymentsType === "PRZELEWY24" || details.header.paymentsType === "STRIPE") {
      Modal.info({
        icon: null,
        destroyOnClose: true,
        content: (
          <Flex
            vertical
            style={{
              paddingBottom: "20px",
            }}
            justify="center"
            align="center"
          >
            <Typography.Title level={5}>{t("newOrderModal.enterPin")}</Typography.Title>
            <Input.OTP
              length={4}
              onChange={(e) => {
                if (e.length === 4) {
                  refundMutateAsync({
                    productOrderId: orderId,
                    pin: e,
                  }).then(() => {
                    updateOrderMutateAsync({
                      id: orderId,
                      body: {
                        status: "DONE",
                      },
                    });
                    sentNotifyMutateAsync({
                      productOrder: orderId,
                      time: "REJECT",
                    });
                  });
                }
              }}
              inputMode="numeric"
            />
          </Flex>
        ),
        footer: (
          <Flex
            style={{
              width: "100%",
            }}
            justify="center"
            align="center"
          >
            {refundIsLoading ? <Spin size="small" /> : <></>}
          </Flex>
        ),
        closable: true,
      });
    }
  }, [orderId, details, updateOrderMutateAsync, sentNotifyMutateAsync, onReject, refundMutateAsync, refundIsLoading]);

  const handleMarkAsCompleted = useCallback(() => {
    if (!orderId) return;

    Modal.confirm({
      title: t("newOrderModal.markCompletedTitle"),
      content: (
        <div style={{ fontSize: 14, color: colors.gray700, lineHeight: 1.6 }}>
          <p style={{ margin: "0 0 8px" }}>
            {t("newOrderModal.markCompletedWarning1Prefix")} <strong>{t("newOrderModal.markCompletedWarning1Strong")}</strong>.
          </p>
          <p style={{ margin: 0 }}>
            {t("newOrderModal.markCompletedWarning2Prefix")} <strong>{t("newOrderModal.markCompletedWarning2Strong")}</strong>.
          </p>
        </div>
      ),
      okText: t("newOrderModal.markCompletedOk"),
      okType: "danger",
      cancelText: t("newOrderModal.cancel"),
      onOk: async () => {
        await updateOrderMutateAsync({
          id: orderId,
          body: {
            status: "DONE",
          },
        });
        onComplete();
      },
    });
  }, [orderId, updateOrderMutateAsync, onComplete]);

  if (!details && isLoadingDetails) {
    return (
      <Modal
        open={open}
        closable={false}
        footer={null}
        width={isMobile ? "100%" : 900}
        style={isMobile ? { top: 0, margin: 0, maxWidth: "100%", height: "100vh" } : { top: 20 }}
      >
        <Flex
          style={{
            width: "100%",
            height: "200px",
          }}
          justify="center"
          align="center"
        >
          <Spin size="large" />
        </Flex>
      </Modal>
    );
  }

  if (!details) return null;

  const isLoading = updateOrderIsLoading || sentNotifyIsLoading || refundIsLoading;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', paddingRight: 24 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
              {t("newOrderModal.newOrder")}
            </div>
            <div style={{ fontSize: 13, color: colors.gray500, fontWeight: 400 }}>
              {dayjs(details.header.createdAt).format("DD-MM-YYYY HH:mm")}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Tag
              style={{
                fontSize: 14,
                padding: "6px 16px",
                fontWeight: 500,
                borderRadius: 6,
                background: details.header.paymentsType === "OFFLINE" ? colors.warningLight : colors.successLight,
                borderColor: details.header.paymentsType === "OFFLINE" ? colors.warning : colors.success,
                color: details.header.paymentsType === "OFFLINE" ? colors.warning : colors.success
              }}
            >
              {`${details.header.paymentsLabel} - ${((details.header.price - calculatedDiscount) / 100).toFixed(2)} ${curSymbol}`}
            </Tag>
            {details.header.appliedCoupon && calculatedDiscount > 0 && (
              <Tag style={{ fontSize: 12, padding: "4px 12px", fontWeight: 500, borderRadius: 6, background: colors.successLight, borderColor: colors.success, color: colors.success }}>
                {t("newOrderModal.couponTag", { code: details.header.appliedCoupon.code, amount: (calculatedDiscount / 100).toFixed(2) })}
              </Tag>
            )}
          </div>
        </div>
      }
      open={open}
      closable={false}
      footer={null}
      width={isMobile ? "100%" : 900}
      style={isMobile ? { top: 0, margin: 0, maxWidth: "100%", height: "100vh" } : { top: 20 }}
      styles={{
        body: isMobile
          ? {
            padding: "20px",
            maxHeight: "calc(100vh - 130px)",
            overflowY: "auto",
          }
          : { padding: "24px" },
      }}
    >
      {/*
        Optymalizacja responsywności: Tablet (md) używa single column, desktop (lg) używa two columns
        Wpływ: Lepsze UX na tabletach z rozdzielczością 768-1024px
      */}
      <Row gutter={16}>
        {/* Left Column: Order Items and Delivery */}
        <Col xs={24} md={24} lg={12}>
          {/* Komentarz klienta */}
          {details.footer.deliveryNote && (
            <>
              <SectionTitle level={5}>{t("newOrderModal.comment")}</SectionTitle>
              <OrderSection style={{ background: "#fffbe6", borderColor: "#ffe58f" }}>
                <Typography.Text style={{ fontSize: 14, color: "#614700" }}>
                  {details.footer.deliveryNote}
                </Typography.Text>
              </OrderSection>
            </>
          )}

          {/* Zamówione pozycje */}
          <SectionTitle level={5}>{t("newOrderModal.orderedItems")}</SectionTitle>
          <OrderSection>
            {(() => {
              // Calculate discount on frontend
              const appliedCoupon = details.header.appliedCoupon;
              const isSpecificProducts = appliedCoupon?.scope === 'SPECIFIC_PRODUCTS';
              const affectedProductIds = appliedCoupon?.affectedProductIds || [];

              // Calculate total price for affected items only
              let affectedItemsTotal = 0;
              groupedItems.forEach(group => {
                const productId = group[0]?.productId;
                const itemPrice = group[0]?.price * group[0]?.quantity;

                // If specific products coupon, only count affected products
                // If cart-wide coupon, count all products
                if (!isSpecificProducts || (productId && affectedProductIds.includes(productId))) {
                  affectedItemsTotal += itemPrice;
                }
              });

              // Calculate total discount amount based on coupon type
              let totalDiscountAmount = calculatedDiscount;

              const itemElements = groupedItems.map((group, groupIndex) => {
                // Get original price for this item
                const itemOriginalPrice = group[0]?.price * group[0]?.quantity;
                const productId = group[0]?.productId;
                let itemDiscountedPrice = itemOriginalPrice;
                let isProductAffected = false;

                if (appliedCoupon && itemOriginalPrice && affectedItemsTotal > 0 && totalDiscountAmount > 0) {
                  // Check if this product is affected by the coupon
                  isProductAffected = !isSpecificProducts || (productId && affectedProductIds.includes(productId));

                  if (isProductAffected) {
                    // Calculate proportional discount for this item based on affected items total
                    const itemRatio = itemOriginalPrice / affectedItemsTotal;
                    const itemDiscount = Math.round(totalDiscountAmount * itemRatio);
                    itemDiscountedPrice = itemOriginalPrice - itemDiscount;
                  }
                }

                return (
                  <OrderItem key={groupIndex}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 0, flex: 1 }}>
                        {group.map((row: any, rowIndex: number) => (
                          <div key={rowIndex} style={{ ...row.style }}>
                            {row.texts.map((text: any, textIndex: number) => {
                              const displayText = text.text.replace(/\(1x\)\s*/g, '');

                              return (
                                <span
                                  key={textIndex}
                                  style={{
                                    ...text.style,
                                    fontSize: 14,
                                  }}
                                >
                                  {displayText}
                                </span>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                      {/* Price display for the first item in group (product) */}
                      {group[0]?.price !== undefined && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: '100px' }}>
                          {appliedCoupon && isProductAffected ? (
                            <>
                              <Typography.Text
                                delete
                                type="secondary"
                                style={{ fontSize: 12 }}
                              >
                                {(itemOriginalPrice / 100).toFixed(2)} {curSymbol}
                              </Typography.Text>
                              <Typography.Text
                                strong
                                style={{ fontSize: 14, color: colors.success }}
                              >
                                {(itemDiscountedPrice / 100).toFixed(2)} {curSymbol}
                              </Typography.Text>
                            </>
                          ) : (
                            <Typography.Text style={{ fontSize: 14, fontWeight: 500 }}>
                              {(itemOriginalPrice / 100).toFixed(2)} {curSymbol}
                            </Typography.Text>
                          )}
                        </div>
                      )}
                    </div>
                  </OrderItem>
                );
              });

              return (
                <>
                  {itemElements}
                  {/* Show coupon discount summary if applicable */}
                  {appliedCoupon && totalDiscountAmount > 0 && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--color-borderLight)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography.Text style={{ fontSize: 13, color: colors.success }}>
                          {t("newOrderModal.couponApplied", { code: appliedCoupon.code })}
                        </Typography.Text>
                        <Typography.Text style={{ fontSize: 13, color: 'var(--color-success)', fontWeight: 500 }}>
                          -{(totalDiscountAmount / 100).toFixed(2)} {curSymbol}
                        </Typography.Text>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </OrderSection>

          {/* Płatność */}
          <SectionTitle level={5}>{t("newOrderModal.payment")}</SectionTitle>
          <OrderSection>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  {t("newOrderModal.method")}
                </Typography.Text>
                <Typography.Text style={{ fontSize: 14, fontWeight: 500 }}>
                  {details.header.paymentsLabel}
                </Typography.Text>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  {t("newOrderModal.status")}
                </Typography.Text>
                <Tag
                  color={
                    details.header.paymentsType === "OFFLINE" ? "gold" : "green"
                  }
                  style={{ margin: 0 }}
                >
                  {details.header.paymentsType === "OFFLINE"
                    ? t("newOrderModal.awaitingPayment")
                    : details.header.paymentsType === "PRZELEWY24" || details.header.paymentsType === "STRIPE"
                    ? t("newOrderModal.paidOnline")
                    : t("newOrderModal.paid")}
                </Tag>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  {t("newOrderModal.amount")}
                </Typography.Text>
                <Typography.Text style={{ fontSize: 14, fontWeight: 500 }}>
                  {(details.header.price / 100).toFixed(2)} {curSymbol}
                </Typography.Text>
              </div>
              {details.header.appliedCoupon && calculatedDiscount > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                    {t("newOrderModal.couponLabel", { code: details.header.appliedCoupon.code })}
                  </Typography.Text>
                  <Typography.Text style={{ fontSize: 14, fontWeight: 500, color: colors.success }}>
                    -{(calculatedDiscount / 100).toFixed(2)} {curSymbol}
                  </Typography.Text>
                </div>
              )}
            </div>
          </OrderSection>

          {/* Dane dostawy */}
          <SectionTitle level={5}>{t("newOrderModal.deliveryData")}</SectionTitle>
          <OrderSection>
            <div>
              <Typography.Text style={{ fontSize: 14, display: "block", marginBottom: 4 }}>
                {details.footer.deliveryType === "SELF_PICKUP"
                  ? t("newOrderModal.selfPickup")
                  : formatAddressWithApartment(details.footer.deliveryLabel, details.footer.apertmenNumber)}
              </Typography.Text>
              {details.footer.tableName && (
                <Typography.Text strong style={{ fontSize: 14, display: "block", marginBottom: 4, color: colors.primary }}>
                  {t("newOrderModal.table", { name: details.footer.tableName })}
                </Typography.Text>
              )}
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                {details.footer.deliveryTimeType === "NOW"
                  ? t("newOrderModal.asapDelivery")
                  : t("newOrderModal.scheduledDelivery", { date: details.footer.deliveryOrderDate })}
              </Typography.Text>
            </div>
          </OrderSection>

          {/* Dane klienta */}
          <SectionTitle level={5}>{t("newOrderModal.customerData")}</SectionTitle>
          <OrderSection>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {details.address.slice(1).map((row: any, index: number) => (
                <div key={index}>
                  {row.texts.map((text: any, textIndex: number) => (
                    <span
                      key={textIndex}
                      style={{
                        ...text.style,
                        fontSize: 14,
                      }}
                    >
                      {text.text}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </OrderSection>

        </Col>

        {/* Right Column: Action Buttons */}
        <Col xs={24} md={24} lg={12}>
          {/* Wybierz czas realizacji */}
          <SectionTitle level={5}>{t("newOrderModal.selectPreparationTime")}</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: 16 }}>
            {TIME_OPTIONS.map((option) => (
              <StyledButton
                key={option.value}
                className="green-button"
                type="primary"
                onClick={() => handleAcceptWithTime(option.value)}
                loading={isLoading}
                disabled={isLoading}
              >
                {option.label}
              </StyledButton>
            ))}

            {/* Przyjmij bez czasu realizacji */}
            <StyledButton
              className="light-green-button"
              onClick={handleConfirmOnly}
              loading={isLoading}
              disabled={isLoading}
            >
              {t("newOrderModal.acceptWithoutTime")}
            </StyledButton>
          </div>

          {/* Inne akcje */}
          <SectionTitle level={5}>{t("newOrderModal.otherActions")}</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <StyledButton
              className="gray-button"
              onClick={onKeepInQueue}
              disabled={isLoading}
            >
              {t("newOrderModal.keepInQueue")}
            </StyledButton>

            <StyledButton
              className="red-button"
              onClick={handleReject}
              loading={isLoading}
              disabled={isLoading}
            >
              {t("newOrderModal.rejectAndRefund")}
            </StyledButton>

            <StyledButton
              className="ghost-danger-button"
              onClick={handleMarkAsCompleted}
              disabled={isLoading}
            >
              {t("newOrderModal.markAsCompleted")}
            </StyledButton>
          </div>
        </Col>
      </Row>

      {/* ID Zamówienia */}
      <div style={{ marginTop: 16, textAlign: 'center', fontSize: 13, color: colors.gray500 }}>
        {t("newOrderModal.orderId", { id: details.id })}
      </div>
    </Modal>
  );
};
